import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useFamily } from '../../context/FamilyContext';
import { formatCurrency } from '../../utils/expenseUtils';
import {
  getDateRangeFromPreset,
  filterExpensesByDateRange,
  computeSummaryStats,
  computeCategoryBreakdown,
  computeTrendData,
  computePerChildBreakdown,
  exportExpensesToCSV,
  downloadCSV,
  DATE_RANGE_PRESETS,
  CATEGORY_COLORS,
} from '../../utils/expenseAnalytics';
import CategoryBreakdown from './CategoryBreakdown';
import SpendingTrend from './SpendingTrend';
import {
  HiCurrencyDollar,
  HiTrendingUp,
  HiTag,
  HiDownload,
  HiChartPie,
  HiFilter,
} from 'react-icons/hi';
import { EXPENSE_CATEGORIES } from '@coparent/shared/src/utils/constants';
import { getCategoryIcon } from '../../utils/expenseUtils';
import { format } from 'date-fns';

const ExpenseInsights = ({ expenses }) => {
  const { family, childrenMap } = useFamily();
  const [dateRange, setDateRange] = useState('last_3');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [categoryDropdownOpen, setCategoryDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setCategoryDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const { startDate, endDate } = useMemo(
    () => getDateRangeFromPreset(dateRange),
    [dateRange]
  );

  const filteredExpenses = useMemo(
    () => filterExpensesByDateRange(expenses, startDate, endDate),
    [expenses, startDate, endDate]
  );

  const stats = useMemo(
    () => computeSummaryStats(filteredExpenses),
    [filteredExpenses]
  );

  const categoryData = useMemo(
    () => computeCategoryBreakdown(filteredExpenses),
    [filteredExpenses]
  );

  const trendExpenses = useMemo(
    () => selectedCategories.length === 0
      ? expenses
      : expenses.filter(e => selectedCategories.includes(e.category || 'other')),
    [expenses, selectedCategories]
  );

  const trendData = useMemo(
    () => computeTrendData(trendExpenses, startDate, endDate, dateRange),
    [trendExpenses, startDate, endDate, dateRange]
  );

  const childData = useMemo(
    () => computePerChildBreakdown(filteredExpenses, childrenMap),
    [filteredExpenses, childrenMap]
  );

  const toggleCategory = (value) => {
    setSelectedCategories(prev =>
      prev.includes(value) ? prev.filter(c => c !== value) : [...prev, value]
    );
  };

  const handleExport = () => {
    const csv = exportExpensesToCSV(filteredExpenses, childrenMap, family?.parentProfiles);
    const filename = `expenses-${format(startDate, 'yyyy-MM-dd')}-to-${format(endDate, 'yyyy-MM-dd')}.csv`;
    downloadCSV(csv, filename);
  };

  return (
    <div className="animate-fade-in space-y-6">
      {/* Date range + category filter */}
      <div className="flex items-center gap-2 flex-wrap pb-1">
        {DATE_RANGE_PRESETS.map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setDateRange(key)}
            className={dateRange === key ? 'chip-active' : 'chip-inactive'}
          >
            {label}
          </button>
        ))}

        <div className="w-px h-6 bg-fog/50 flex-shrink-0 mx-1" />

        <div className="relative flex-shrink-0" ref={dropdownRef}>
          <button
            onClick={() => setCategoryDropdownOpen(prev => !prev)}
            className={selectedCategories.length > 0 ? 'chip-active flex items-center gap-1.5' : 'chip-inactive flex items-center gap-1.5'}
          >
            <HiFilter className="text-sm" />
            {selectedCategories.length === 0
              ? 'All Categories'
              : `${selectedCategories.length} ${selectedCategories.length === 1 ? 'Category' : 'Categories'}`}
          </button>

          {categoryDropdownOpen && (
            <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-xl shadow-warm-lg border border-surface-200 py-2 z-50">
              {selectedCategories.length > 0 && (
                <>
                  <button
                    onClick={() => setSelectedCategories([])}
                    className="w-full text-left px-4 py-2 text-xs font-semibold text-meadow hover:bg-surface-50 transition-colors"
                  >
                    Clear all
                  </button>
                  <div className="border-t border-surface-100 my-1" />
                </>
              )}
              {EXPENSE_CATEGORIES.map(({ value, label }) => {
                const isSelected = selectedCategories.includes(value);
                return (
                  <button
                    key={value}
                    onClick={() => toggleCategory(value)}
                    className="w-full text-left px-4 py-2 text-sm hover:bg-surface-50 transition-colors flex items-center gap-2.5"
                  >
                    <span className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0"
                      style={{
                        borderColor: isSelected ? '#1B6B4A' : '#d6d3cd',
                        backgroundColor: isSelected ? '#1B6B4A' : 'transparent',
                      }}
                    >
                      {isSelected && (
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </span>
                    <span>{getCategoryIcon(value)}</span>
                    <span className="text-ink-700">{label}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {filteredExpenses.length === 0 ? (
        <div className="card p-12 text-center">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-surface-100 mb-4">
            <HiChartPie className="text-2xl text-ink-300" />
          </div>
          <p className="text-ink-400 font-medium">No expenses in this period</p>
          <p className="text-ink-300 text-sm mt-1">Try selecting a wider date range</p>
        </div>
      ) : (
        <>
          {/* Summary stat cards */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="card p-5">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-8 h-8 rounded-lg bg-canopy/10 flex items-center justify-center">
                  <HiCurrencyDollar className="text-canopy text-base" />
                </div>
                <span className="text-xs text-ink-400 font-semibold uppercase tracking-wider">Total Spent</span>
              </div>
              <p className="text-2xl font-bold text-ink-900 tracking-tight">{formatCurrency(stats.totalSpent)}</p>
              <p className="text-xs text-ink-400 mt-1">{stats.expenseCount} expenses</p>
            </div>

            <div className="card p-5">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-8 h-8 rounded-lg bg-meadow/10 flex items-center justify-center">
                  <HiTrendingUp className="text-meadow text-base" />
                </div>
                <span className="text-xs text-ink-400 font-semibold uppercase tracking-wider">Avg / Month</span>
              </div>
              <p className="text-2xl font-bold text-ink-900 tracking-tight">{formatCurrency(stats.averageMonthly)}</p>
              <p className="text-xs text-ink-400 mt-1">Monthly average</p>
            </div>

            <div className="card p-5">
              <div className="flex items-center gap-2.5 mb-3">
                <div className="w-8 h-8 rounded-lg bg-sunbeam/10 flex items-center justify-center">
                  <HiTag className="text-sunbeam text-base" />
                </div>
                <span className="text-xs text-ink-400 font-semibold uppercase tracking-wider">Top Category</span>
              </div>
              {stats.topCategory ? (
                <>
                  <p className="text-2xl font-bold text-ink-900 tracking-tight">{stats.topCategory.icon} {stats.topCategory.label}</p>
                  <p className="text-xs text-ink-400 mt-1">{formatCurrency(stats.topCategory.amount)}</p>
                </>
              ) : (
                <p className="text-ink-300 text-sm">—</p>
              )}
            </div>
          </div>

          {/* Charts */}
          <SpendingTrend data={trendData} dateRange={dateRange} />
          <CategoryBreakdown data={categoryData} />

          {/* Per-child breakdown */}
          {childData.length > 0 && (
            <div>
              <h3 className="font-bold text-ink-900 text-[0.9375rem] mb-4">Spending by Child</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {childData.map((child) => (
                  <div key={child.childId} className="card p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-bold"
                        style={{ backgroundColor: child.childColor }}
                      >
                        {child.childName[0]}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-ink-800 text-sm">{child.childName}</p>
                        <p className="text-xs text-ink-400">{formatCurrency(child.total)} total</p>
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      {child.categories.slice(0, 4).map((cat) => (
                        <div key={cat.category} className="flex items-center gap-2">
                          <div
                            className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: CATEGORY_COLORS[cat.category] || '#78716c' }}
                          />
                          <span className="text-xs text-ink-500 flex-1 truncate">{cat.label}</span>
                          <span className="text-xs font-semibold text-ink-700 tabular-nums">
                            {formatCurrency(cat.amount)}
                          </span>
                        </div>
                      ))}
                      {child.categories.length > 4 && (
                        <p className="text-xs text-ink-300 pl-4">
                          +{child.categories.length - 4} more categories
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Export button */}
          <div className="flex justify-center pt-2 pb-4">
            <button
              onClick={handleExport}
              className="btn-secondary flex items-center gap-2 text-sm"
            >
              <HiDownload className="text-base" />
              Export to CSV
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ExpenseInsights;
